import React from "react";
import { Chip } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

import type { GoalDomain as GoalDomainId } from "api/types/aba";

export const DOMAINS = {
  communication: { id: "communication", label: "Communication" },
  community: { id: "community", label: "Community" },
  adaptive: { id: "adaptive", label: "Adaptive" },
  "behavior-reduction": { id: "behaviorReduction", label: "Behavior Reduction" },
  "social-skills": { id: "socialSkills", label: "Social Skills" },
  "executive-functioning": { id: "executiveFunctioning", label: "Executive Functioning" },
} as const;

export interface DomainPillProps {
  /** The clinical domain id to display */
  domain: GoalDomainId;
  /** Additional MUI sx styles */
  sx?: SxProps<Theme>;
}

interface DomainStyleConfig {
  bgcolor: string;
  borderColor: string;
  color: string;
}

const getDomainStyles = (theme: Theme): Record<GoalDomainId, DomainStyleConfig> => {
  // Cast to any to access extended palette colors from @answersnow/pkg-theme
  const p = theme.palette as any;

  return {
    communication: {
      bgcolor: p.blue?.[50] ?? "#e3f2fd",
      borderColor: p.blue?.[200] ?? "#90caf9",
      color: p.blue?.[500] ?? "#2196f3",
    },
    community: {
      bgcolor: p.purple?.[50] ?? "#faf7fd",
      borderColor: p.purple?.[200] ?? "#eadbf9",
      color: theme.palette.primary.main,
    },
    adaptive: {
      bgcolor: p.success?.background ?? "#e8f5e9",
      borderColor: theme.palette.success.main,
      color: theme.palette.success.main,
    },
    "behavior-reduction": {
      bgcolor: p.warning?.background ?? "#fff3e0",
      borderColor: theme.palette.warning.main,
      color: theme.palette.warning.main,
    },
    "social-skills": {
      bgcolor: p.pink?.[50] ?? "#fce4ec",
      borderColor: p.pink?.[200] ?? "#f48fb1",
      color: p.pink?.[500] ?? "#e91e63",
    },
    "executive-functioning": {
      bgcolor: p.indigo?.[50] ?? "#e8eaf6",
      borderColor: p.indigo?.[200] ?? "#9fa8da",
      color: p.indigo?.[500] ?? "#3f51b5",
    },
  };
};

/**
 * Displays a colored pill chip representing a clinical domain type.
 * Each domain has a distinct color scheme for quick visual identification.
 *
 * @param props
 * @param props.domain - The clinical domain id (key of DOMAINS)
 * @param [props.sx] - Additional MUI sx styles
 */
const DomainPill: React.FC<DomainPillProps> = ({ domain, sx }) => {
  return (
    <Chip
      label={DOMAINS[domain].label}
      variant="outlined"
      size="small"
      aria-label={`Domain: ${DOMAINS[domain].label}`}
      sx={[
        (theme) => {
          const { bgcolor, borderColor, color } = getDomainStyles(theme)[domain];
          return {
            borderColor,
            color,
            borderRadius: "6px",
            height: "auto",
            py: "2px",
            px: "3px",
            "&.MuiChip-outlinedDefault": {
              bgcolor: bgcolor,
            },
            "& .MuiChip-label": {
              px: "6px",
              py: 0,
              typography: "caption",
              lineHeight: 1.4,
            },
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
};

export default DomainPill;
